// Role List Component - Displays list of roles

import { Edit, Shield, Trash2 } from 'lucide-react';
import React from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import type { Role } from './types';

type RoleListProps = {
  roles: Role[];
  onEdit: (role: Role) => void;
  onDelete: (roleId: string) => void;
};

export const RoleList: React.FC<RoleListProps> = ({
  roles,
  onEdit,
  onDelete,
}) => {
  if (roles.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12 text-center'>
        <Shield className='h-12 w-12 text-muted-foreground mb-4' />
        <h3 className='text-lg font-semibold mb-2'>No roles found</h3>
        <p className='text-sm text-muted-foreground'>
          Create your first role to get started with role-based access control
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Role Name</TableHead>
          <TableHead>Role Code</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className='text-right'>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {roles.map(role => (
          <TableRow key={role.roleId}>
            <TableCell className='font-medium'>{role.roleName}</TableCell>
            <TableCell>
              <code className='text-xs bg-muted px-2 py-1 rounded'>
                {role.roleCode}
              </code>
            </TableCell>
            <TableCell>
              <Badge
                variant={role.roleType === 'system' ? 'default' : 'secondary'}
              >
                {role.roleType}
              </Badge>
            </TableCell>
            <TableCell className='text-muted-foreground'>
              {role.description || '-'}
            </TableCell>
            <TableCell>
              <Badge variant={role.isActive ? 'default' : 'secondary'}>
                {role.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
            <TableCell className='text-right'>
              <div className='flex items-center justify-end gap-2'>
                <Button variant='ghost' size='sm' onClick={() => onEdit(role)}>
                  <Edit className='h-4 w-4' />
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => onDelete(role.roleId)}
                >
                  <Trash2 className='h-4 w-4 text-destructive' />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
